import { React, useEffect, useState } from "react";
import styles from "./learningZonesPage.module.css";
import SearchComp from "../../../components/search/SearchComp";
import LanguagePalsComp from "../../../components/languagePals/LanguagePalsComp";
import axios from "axios";
import LearningZonesTab from "../../../components/learningZonesTab/LearningZonesTab";
import ConfirmationModal from "../../../components/confirmationModal/ConfirmationModal";
import { FaGraduationCap } from "react-icons/fa6";

const apiUrl = process.env.REACT_APP_URL;

const LearningZonesPage = () => {
  /* The Learning Zones Page contains two tabs - Creator tab, which has a list of learning zones the user is the creator of, and Member tab, which has a list of learning zones the user is member of, but did not create. For each learning zone, the following details are displayed: name, learned language, creator username, creator profile image, number of members, and the last update date. On this page, a user can remove a learning zone they created, or leave a learning zone they did not create. The user can also create a new learning zone. */

  const [activeTab, setActiveTab] = useState("creator");
  const [username, setUsername] = useState("");
  const [creatorLearningZones, setCreatorLearningZones] = useState([]);
  const [memberLearningZones, setMemberLearningZones] = useState([]);
  const [allLearningZones, setAllLearningZones] = useState([]);
  const [filteredLearningZones, setFilteredLearningZones] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedZoneId, setSelectedZoneId] = useState("");
  const [showModal, setShowModal] = useState(false);

  /* Get a list of learning zones for both the Creator tab and the Member tab. For each learning zone, only basic information is fetched from the DB. */
  const fetchData = async () => {
    try {
      const res = await axios.get(`${apiUrl}/auth/user`, {
        withCredentials: true,
      });

      if (res.status === 200 && res.data.success) {
        setUsername(res.data.username);

        const creatorRes = await axios.get(
          `${apiUrl}/learning_zones/creator/${res.data.username}`,
          {
            withCredentials: true,
          }
        );

        const memberRes = await axios.get(
          `${apiUrl}/learning_zones/member/${res.data.username}`,
          {
            withCredentials: true,
          }
        );

        if (creatorRes.data && memberRes.data) {
          setCreatorLearningZones(creatorRes.data.learning_zones || []);
          setMemberLearningZones(memberRes.data.learning_zones || []);
        } else {
          console.error("Error in obtaining user learning zones");
        }
      }
    } catch (error) {
      console.error("Error in fetching data:", error);
    }
  };

  /* If the logged in user is an admin, get the list of all active learning zones on the website. For each learning zone, only basic information is fetched from the DB. */
  const fetchAdminData = async () => {
    try {
      const res = await axios.get(`${apiUrl}/auth/admin`, {
        withCredentials: true,
      });

      if (res.status === 200 && res.data.success && res.data.role === "Admin") {
        const allLZRes = await axios.get(`${apiUrl}/learning_zones/all`, {
          withCredentials: true,
        });

        if (allLZRes.data && allLZRes.data.success) {
          setAllLearningZones(allLZRes.data.learning_zones || []);
          setFilteredLearningZones(allLZRes.data.learning_zones || []);

          if (allLZRes.data.learning_zones.length > 0) {
            setSelectedZoneId(allLZRes.data.learning_zones[0].learning_zone_id);
          }
        }
      }
    } catch (error) {
      console.error("Error in fetching admin data:", error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAdminData(); // Fetch admin data if the logged in user is an admin
  }, []);

  /**
   * Handle switching between tabs
   * @param {*} tab - the new tab the user selected
   */
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    fetchData(); // Reload data when the tab is switched
  };

  /**
   * Open a specified learning zone page, in a new tab.
   * @param {*} learningZoneId - the id of the learning zone page to open
   */
  const visitLearningZone = (learningZoneId) => {
    if (!learningZoneId) {
      return;
    }

    window.open(
      `/learningZone/${learningZoneId}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /**
   * Open the delete confirmation modal for the admin to confirm the deactivation of a learning zone.
   * @param {*} learningZoneId - the id of the learning zone to confirm deactivation of.
   */
  const handleDeleteClick = (learningZoneId) => {
    setShowModal(true);
    setSelectedZoneId(learningZoneId);
  };

  /**
   * Close the delete confirmation modal.
   */
  const handleCancelDelete = async () => {
    setShowModal(false);
  };

  /**
   * Deactivate the learning zone with the id of selectedZoneId
   */
  const deleteLearningZone = async () => {
    if (!selectedZoneId) {
      return;
    }

    try {
      const res = await axios.put(
        `${apiUrl}/learning_zones/deactivate/${selectedZoneId}`,
        {
          withCredentials: true,
        }
      );

      if (res.status === 200 && res.data.success) {
        setShowModal(false);
        fetchData();
        fetchAdminData();
        if (filteredLearningZones.length > 0) {
          setSelectedZoneId(filteredLearningZones[0]);
        } else {
          setSelectedZoneId(null);
        }
      } else {
        console.error("Error in deleting learning zone:", res.data.message);
      }
    } catch (error) {
      console.error("Error in deleting learning zone:", error);
    }
  };

  /**
   * Close the notification modal when clicking outside of it, while the notification status stays unread.
   */
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowModal(false); // Close the modal without marking notification as read or declining the request
    }
  };

  /* Define the delete confirmation message */
  const modalContent = {
    message:
      "Are you sure you want to delete learning zone number " +
      selectedZoneId +
      "?",
  };

  const handleFilterTextChange = (e) => {
    setFilterText(e.target.value);

    const newFilteredLearningZones = allLearningZones.filter((zone) =>
      `${zone.learning_zone_id} - ${zone.learning_zone_name} (${zone.creator_username})`
        .toLowerCase()
        .includes(e.target.value.toLowerCase())
    );

    setFilteredLearningZones(newFilteredLearningZones);
  };

  useEffect(() => {
    if (filteredLearningZones.length > 0) {
      setSelectedZoneId(filteredLearningZones[0].learning_zone_id);
    } else {
      setSelectedZoneId(null);
    }
  }, [filterText, filteredLearningZones]);

  return (
    <div className={styles.main}>
      {/* main layout  */}
      <section className={styles.mainLayout}>
        {/* left section  */}
        <div className={styles.left}>
          <SearchComp />
        </div>
        {/* middle section  */}
        <div className={styles.middle}>
          <section className={styles.learningZones}>
            <h1>
              Learning Zones{" "}
              <span className={styles.headerIcon}>
                <FaGraduationCap />
              </span>
            </h1>
            <div className={styles.learningZonesArea}>
              <div className={styles.learningZonesTabs}>
                <button
                  onClick={() => handleTabClick("creator")}
                  className={`${styles.tabButton} ${
                    activeTab === "creator" ? styles.active : ""
                  }`}
                >
                  Creator
                </button>
                <button
                  onClick={() => handleTabClick("member")}
                  className={`${styles.tabButton} ${
                    activeTab === "member" ? styles.active : ""
                  }`}
                >
                  Member
                </button>
              </div>
              <div className={styles.content}>
                {activeTab === "creator" && (
                  <LearningZonesTab
                    tabData={creatorLearningZones}
                    isCreator={true}
                    username={username}
                    refreshData={fetchData}
                  />
                )}
                {activeTab === "member" && (
                  <LearningZonesTab
                    tabData={memberLearningZones}
                    isCreator={false}
                    username={username}
                    refreshData={fetchData}
                  />
                )}
              </div>
            </div>
            {activeTab === "creator" &&
              allLearningZones &&
              allLearningZones.length > 0 && (
                <div className={styles.allLearningZones}>
                  <p>All active learning zones:</p>

                  <input
                    type="text"
                    placeholder="Filter the list by learning zone name or ID, or by creator username"
                    value={filterText}
                    onChange={(e) => handleFilterTextChange(e)}
                  />
                  <div className={styles.selectWrapper}>
                    <select
                      name="learningZones"
                      value={selectedZoneId ? selectedZoneId : undefined}
                      onChange={(e) => setSelectedZoneId(e.target.value)}
                    >
                      {filteredLearningZones.map((zone) => (
                        <option
                          key={zone.learning_zone_id}
                          value={zone.learning_zone_id}
                        >
                          {zone.learning_zone_id} - {zone.learning_zone_name}{" "}
                          (by {zone.creator_username})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => visitLearningZone(selectedZoneId)}
                      disabled={!selectedZoneId}
                    >
                      Visit
                    </button>
                    <button
                      className={styles.deleteBtn}
                      onClick={() => handleDeleteClick(selectedZoneId)}
                      disabled={!selectedZoneId}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
          </section>
          {showModal && modalContent && (
            <ConfirmationModal
              content={modalContent}
              approveBtnText="Delete"
              cancelBtnText="Cancel"
              show={showModal}
              onClose={handleCancelDelete}
              onConfirm={deleteLearningZone}
              backdropClick={handleBackdropClick}
            />
          )}
        </div>
        {/* right section  */}
        <div className={styles.right}>
          <LanguagePalsComp isOwn={true} />
        </div>
      </section>
    </div>
  );
};

export default LearningZonesPage;
